import UserApiBase from "../base/UserApiBase";

import { type UserImageMedia } from "@/schemas/user/image";
import { type OkResponse } from "@/schemas/api/ok";
import {
  type PaginatedRequest,
  type PaginatedResponse,
} from "@/schemas/api/pagination";
import { type AppUserSession } from "@/schemas/auth/session";
import {
  type InteractionRequest,
  InteractionType,
  type SendWinkRequest,
} from "@/schemas/user/interaction";
import { type UserProfilePreview } from "@/schemas/user/preview";

class InteractionService extends UserApiBase {
  public constructor(session: AppUserSession) {
    super("/interaction", session);
  }

  public getUserLikedItems(
    target: string,
  ): Promise<PaginatedResponse<UserImageMedia>> {
    return this.get(`/${target}`);
  }

  private async interactWithItem(
    params: InteractionRequest,
  ): Promise<OkResponse> {
    const queryString = new URLSearchParams({
      item_id: params.itemId,
      type: params.type,
      undo: params.undo.toString(),
    }).toString();

    return await this.post<OkResponse>(`/?${queryString}`, {});
  }

  public async like(item: UserImageMedia): Promise<OkResponse> {
    return this.interactWithItem({
      itemId: item.id,
      type: InteractionType.LIKE,
      undo: false,
    });
  }

  public async unlike(item: UserImageMedia): Promise<OkResponse> {
    return this.interactWithItem({
      itemId: item.id,
      type: InteractionType.LIKE,
      undo: true,
    });
  }

  public linkOrUnlike(
    currentIsLiked: boolean,
    item: UserImageMedia,
  ): Promise<OkResponse> {
    if (currentIsLiked === true) {
      return this.unlike(item);
    } else {
      return this.like(item);
    }
  }

  // Todo; this should send a message consisting of the wink versus the notification.
  public async sendWink(target: string): Promise<OkResponse> {
    const payload: SendWinkRequest = {
      target,
    };
    return this.post(`/wink`, { ...payload });
  }

  public async getImageLikes(
    image: UserImageMedia,
    page: PaginatedRequest,
  ): Promise<PaginatedResponse<UserProfilePreview>> {
    const paginateQueryStr = this.buildPaginateQueryStr(page);
    return this.get(
      `/media/${image.id}${paginateQueryStr}&type=${InteractionType.LIKE}`,
    );
  }
}

export default InteractionService;
